import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { MenuItem, MessageService } from 'primeng/api';
import { Location, LocationMerchandiserAssignment } from '../location.model';
import { LocationService } from '../location.service';
import { LocationMergeService } from '../location-merge.service';
import { Customer } from '../../customer/customer.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from 'src/app/shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';

@Component({
    selector: 'app-view-location',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        BreadcrumbModule,
        ProgressBarModule,
        TagModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent
    ],
    providers: [MessageService, BaseMasterDataService],
    templateUrl: './view-location.component.html',
    styleUrl: './view-location.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewLocationComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private locationService = inject(LocationService);
    private mergeService = inject(LocationMergeService);
    private messageService = inject(MessageService);
    private sessionStorage = inject(SessionStorageService);
    private sanitizer = inject(DomSanitizer);
    private baseMasterDataService = inject(BaseMasterDataService);

    location = signal<Location | null>(null);
    cityName = signal('');
    loading = signal(false);
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Locations', routerLink: '/location' },
            { label: 'View' }
        ];
        this.loadLocation();
    }

    loadLocation(): void {
        const locationId = Number(this.route.snapshot.paramMap.get('id'));
        if (!locationId) {
            this.back();
            return;
        }
        this.loading.set(true);
        this.locationService.getLocationById(locationId).subscribe({
            next: response => {
                const location = response?.data?.[0] || null;
                if (!location) {
                    const companyId = this.sessionStorage.getCompany()?.id;
                    if (companyId) {
                        this.mergeService.resolve(companyId, locationId).subscribe({
                            next: result => {
                                this.loading.set(false);
                                if (result.locationId !== locationId) {
                                    this.router.navigate(['/location/view', result.locationId]).then(() => this.loadLocation());
                                }
                            },
                            error: () => this.loading.set(false)
                        });
                        return;
                    }
                }
                this.location.set(location);
                this.loadCityName(location);
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Location',
                    detail: 'Unable to load location details.'
                });
            }
        });
    }

    back(): void {
        this.router.navigate(['/location']);
    }

    edit(): void {
        const locationId = this.location()?.id;
        if (locationId) {
            this.router.navigate(['/location/edit', locationId]);
        }
    }

    title(): string {
        return this.location()?.name || 'Location';
    }

    subtitle(): string {
        return this.addressLabel() || 'Review the master location used by shipments and operations.';
    }

    locationCode(): string {
        const location = this.location();
        return location?.externalLocationReference || (location?.id ? `LOC-${location.id}` : '-');
    }

    addressLabel(): string {
        const address = this.location()?.address;
        const parts = [
            address?.getSubLocality?.(),
            address?.getLocality?.(),
            this.location()?.country?.name
        ].filter(Boolean);
        return parts.join(', ');
    }

    cityLabel(): string {
        const location = this.location();
        return this.cityName() || location?.address?.getLocality?.() || '-';
    }

    coordinatesLabel(): string {
        const coordinates = this.location()?.location;
        if (!coordinates?.latitude || !coordinates?.longitude) {
            return '-';
        }
        return `${coordinates.latitude}, ${coordinates.longitude}`;
    }

    hasCoordinates(): boolean {
        const coordinates = this.location()?.location;
        return !!coordinates?.latitude && !!coordinates?.longitude;
    }

    mapUrl(): SafeResourceUrl | null {
        const coordinates = this.location()?.location;
        if (!coordinates?.latitude || !coordinates?.longitude) {
            return null;
        }
        const latitude = Number(coordinates.latitude);
        const longitude = Number(coordinates.longitude);
        return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`
        );
    }

    openExternalMap(): void {
        const coordinates = this.location()?.location;
        if (!coordinates?.latitude || !coordinates?.longitude) {
            return;
        }
        window.open(`https://maps.google.com/?q=${coordinates.latitude},${coordinates.longitude}`, '_blank');
    }

    defaultContactLabel(): string {
        const contact = this.location()?.defaultContact;
        return this.contactLabel(contact);
    }

    contactsSummary(): string {
        const contacts = this.location()?.contacts || [];
        if (!contacts.length) {
            return '-';
        }
        return contacts.map(contact => this.contactLabel(contact)).join(' ; ');
    }

    private contactLabel(contact?: Customer): string {
        return [contact?.name, contact?.emailAddress, contact?.phoneNumber]
            .filter(Boolean)
            .join(' | ') || '-';
    }

    merchandiserAssignments(): LocationMerchandiserAssignment[] {
        return this.location()?.merchandiserAssignments || [];
    }

    merchandiserAssignmentClientLabel(assignment: LocationMerchandiserAssignment): string {
        return assignment.crmClient?.companyName || (assignment.crmClientId ? `CRM Client #${assignment.crmClientId}` : '-');
    }

    merchandiserAssignmentAccountLabel(assignment: LocationMerchandiserAssignment): string {
        return assignment.crmAccount?.companyName || (assignment.crmAccountId ? `CRM Account #${assignment.crmAccountId}` : '-');
    }

    merchandiserAssignmentMerchandiserLabel(assignment: LocationMerchandiserAssignment): string {
        return assignment.merchandiser?.name || (assignment.merchandiserId ? `Merchandiser #${assignment.merchandiserId}` : '-');
    }

    merchandiserAssignmentRoleLabel(assignment: LocationMerchandiserAssignment): string {
        const parts = [
            assignment.accessRole,
            assignment.primaryMerchandiser ? 'Primary' : null,
            assignment.active === false ? 'Inactive' : 'Active'
        ].filter(Boolean);
        return parts.join(' | ') || '-';
    }

    locationTypeLabel(): string {
        return this.location()?.locationType || '-';
    }

    statusLabel(): string {
        return this.location()?.locationType ? 'Active' : 'Draft';
    }

    companyName(): string {
        return this.location()?.company?.name || this.sessionStorage.getCompany()?.name || '-';
    }

    private loadCityName(location: Location | null): void {
        this.cityName.set('');
        const cityId = location?.cityId;
        const countryId = location?.countryId ?? location?.country?.id;
        if (!cityId || !countryId) {
            return;
        }
        this.baseMasterDataService.getCities('en-US', countryId).subscribe({
            next: cities => {
                if (this.location()?.id !== location?.id) {
                    return;
                }
                this.cityName.set(cities.find(city => city.id === cityId)?.cityName || '');
            }
        });
    }
}
